<template>
    <div class="book-container">
        <!-- 翻书页面显示 -->
        <div class="book-display">
            <!-- 翻书容器 -->
            <div class="book" :class="{ 'flipping': isFlipping }" ref="bookRef">
                <!-- 左侧页面 -->
                <div class="page left-page" :style="leftPageStyle">
                    <div class="page-content">
                        <div class="page-number left">{{ currentPage }}</div>
                        <img
                                v-if="currentPage >= 0"
                                :src="data[currentPage]"
                                :alt="data[currentPage]"
                                class="page-image"
                        />
                    </div>
                </div>

                <!-- 右侧页面 -->
                <div class="page right-page" :style="rightPageStyle">
                    <div class="page-content">
                        <div class="page-number right">{{ currentPage + 1 }}</div>
                        <img
                                v-if="currentPage + 1 < totalPages"
                                :src="data[currentPage + 1]"
                                :alt="data[currentPage + 1]"
                                class="page-image"
                        />
                    </div>
                </div>

                <!-- 翻页动画层 -->
                <div class="flip-page" :style="flipPageStyle" @click="flipRight" v-if="canFlipNext">
                    <div class="flip-page-front">
                        <div class="flip-content">
                            <img
                                    v-if="currentPage + 1 < totalPages"
                                    :src="data[currentPage + 1]"
                                    :alt="data[currentPage + 1]"
                                    class="flip-image"
                            />
                            <div class="flip-page-number">{{ currentPage + 1 }}</div>
                        </div>
                    </div>
                    <div class="flip-page-back">
                        <div class="flip-content">
                            <img
                                    v-if="currentPage + 2 < totalPages"
                                    :src="data[currentPage + 2]"
                                    :alt="data[currentPage + 2]"
                                    class="flip-image"
                            />
                            <div class="flip-page-number">{{ currentPage + 2 }}</div>
                        </div>
                    </div>
                </div>

                <!-- 左翻页层 -->
                <div class="flip-page left-flip" :style="leftFlipPageStyle" @click="flipLeft" v-if="canFlipPrev">
                    <div class="flip-page-front">
                        <div class="flip-content">
                            <img
                                    v-if="currentPage >= 0"
                                    :src="data[currentPage]"
                                    :alt="data[currentPage]"
                                    class="flip-image"
                            />
                            <div class="flip-page-number">{{ currentPage }}</div>
                        </div>
                    </div>
                    <div class="flip-page-back">
                        <div class="flip-content">
                            <img
                                    v-if="currentPage - 1 >= 0"
                                    :src="data[currentPage - 1]"
                                    :alt="data[currentPage - 1]"
                                    class="flip-image"
                            />
                            <div class="flip-page-number">{{ currentPage - 1 }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref,
        // reactive,
        defineProps,
        computed, onMounted, onUnmounted, watch } from 'vue';
    import { message } from 'ant-design-vue';
    // Props：接收图片数据
    const props = defineProps({
        data: Array
    });

    // 响应式数据
    const currentPage = ref(0);
    const isFlipping = ref(false);
    const isAutoPlaying = ref(false);
    const flipSpeed = ref(800);
    const autoPlayInterval = ref(3000);
    // const showPageNumbers = ref(true);
    const enableShadows = ref(true);
    const enableSound = ref(false);
    const bookRef = ref(null);

    // 自动播放定时器
    let autoPlayTimer = null;

    // 计算属性
    const totalPages = computed(() => props.data.length);
    const canFlipNext = computed(() => currentPage.value + 2 < totalPages.value);
    const canFlipPrev = computed(() => currentPage.value > 0);

    // 页面样式
    const leftPageStyle = computed(() => {
        return {
            'box-shadow': enableShadows.value ? 'inset -5px 0 15px rgba(0,0,0,0.1)' : 'none'
        };
    });

    const rightPageStyle = computed(() => {
        return {
            'box-shadow': enableShadows.value ? 'inset 5px 0 15px rgba(0,0,0,0.1)' : 'none'
        };
    });

    // 翻页动画样式
    const flipPageStyle = computed(() => {
        return {
            transition: `transform ${flipSpeed.value}ms cubic-bezier(0.42, 0, 0.58, 1)`,
            'z-index': isFlipping.value ? 100 : 10
        };
    });

    const leftFlipPageStyle = computed(() => {
        return {
            transition: `transform ${flipSpeed.value}ms cubic-bezier(0.42, 0, 0.58, 1)`,
            'z-index': isFlipping.value ? 100 : 10
        };
    });

    // 翻页音效
    const playFlipSound = () => {
        if (!enableSound.value) return;

        // 创建翻页音效
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(100, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.1);

        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
    };

    // 翻页方法
    const flipRight = () => {
        if (isFlipping.value || !canFlipNext.value) return;

        isFlipping.value = true;
        playFlipSound();

        // 触发翻页动画
        // transform: rotateY(-150deg);
        // z-index: 3;
        // box-shadow:
        // 5px 0 30px rgba(0,0,0,0.2),
        // inset -10px 0 50px rgba(0,0,0,0.05);
        const flipPage = bookRef.value?.querySelector('.flip-page:not(.left-flip)');
        if (flipPage) {
            flipPage.style.transform = 'rotateY(-150deg)';
            flipPage.style['z-index'] = '3';
            flipPage.style['box-shadow'] = '5px 0 30px rgba(0,0,0,0.2)';
            flipPage.style['inset'] = '-10px 0 50px rgba(0,0,0,0.05)';
        }

        // 动画完成后更新页码
        setTimeout(() => {
            currentPage.value += 1;
            isFlipping.value = false;

            // 重置翻页元素
            if (flipPage) {
                flipPage.style.transform = 'rotateY(0deg)';
            }
        }, flipSpeed.value);
    };

    const flipLeft = () => {
        if (isFlipping.value || !canFlipPrev.value) return;

        isFlipping.value = true;
        playFlipSound();

        // 触发左翻页动画
        const flipPage = bookRef.value?.querySelector('.left-flip');
        if (flipPage) {
            flipPage.style.transform = 'rotateY(180deg)';
        }

        // 动画完成后更新页码
        setTimeout(() => {
            currentPage.value -= 2;
            isFlipping.value = false;

            // 重置翻页元素
            if (flipPage) {
                flipPage.style.transform = 'rotateY(0deg)';
            }
        }, flipSpeed.value);
    };

    // 导航方法
    const nextPage = () => {
        if (currentPage.value + 2 < totalPages.value) {
            flipRight();
        } else if (currentPage.value + 1 < totalPages.value) {
            currentPage.value += 1;
            message.info('已到达最后一页');
        }
    };

    const prevPage = () => {
        if (currentPage.value > 0) {
            flipLeft();
        } else {
            message.info('已到达第一页');
        }
    };

    const goToPage = (pageIndex) => {
        if (pageIndex < 0 || pageIndex >= totalPages.value) return;

        // 如果是偶数页，直接跳转
        if (pageIndex % 2 === 0) {
            currentPage.value = pageIndex;
        }
        // 如果是奇数页，跳转到前一页（因为书是左右两页显示）
        else {
            currentPage.value = pageIndex - 1;
        }
    };

    // 自动播放
    const autoPlay = () => {
        if (isAutoPlaying.value) {
            stopAutoPlay();
        } else {
            startAutoPlay();
        }
    };

    const startAutoPlay = () => {
        isAutoPlaying.value = true;
        autoPlayTimer = setInterval(() => {
            if (currentPage.value + 2 < totalPages.value) {
                flipRight();
            } else {
                // stopAutoPlay();
                // message.info('已播放到最后，自动播放停止');
                currentPage.value = 0;
            }
        }, autoPlayInterval.value);
    };

    const stopAutoPlay = () => {
        isAutoPlaying.value = false;
        if (autoPlayTimer) {
            clearInterval(autoPlayTimer);
            autoPlayTimer = null;
        }
    };


    // 键盘控制
    const handleKeyDown = (event) => {
        switch(event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                prevPage();
                break;
            case 'ArrowRight':
            case ' ':
                event.preventDefault();
                nextPage();
                break;
            case 'Home':
                event.preventDefault();
                goToPage(0);
                break;
            case 'End':
                event.preventDefault();
                goToPage(totalPages.value - 1);
                break;
        }
    };

    // 鼠标滚轮控制
    const handleWheel = (event) => {
        event.preventDefault();
        if (event.deltaY > 0) {
            nextPage();
        } else {
            prevPage();
        }
    };

    // 生命周期
    onMounted(() => {
        window.addEventListener('keydown', handleKeyDown);
        const bookElement = bookRef.value;
        if (bookElement) {
            bookElement.addEventListener('wheel', handleWheel, { passive: false });
        }

        autoPlay();
    });

    onUnmounted(() => {
        window.removeEventListener('keydown', handleKeyDown);
        stopAutoPlay();

        const bookElement = bookRef.value;
        if (bookElement) {
            bookElement.removeEventListener('wheel', handleWheel);
        }
    });

    // 监听自动播放间隔变化
    watch(autoPlayInterval, () => {
        if (isAutoPlaying.value) {
            stopAutoPlay();
            startAutoPlay();
        }
    });

    // // 添加新图片
    // const addNewPage = (url, title) => {
    //     pages.value.push({
    //         url: url || `https://picsum.photos/600/800?random=${pages.value.length + 1}`,
    //         title: title || `新页面 ${pages.value.length + 1}`,
    //         description: '新增的页面'
    //     });
    //     message.success('已添加新页面');
    // };
</script>

<style scoped>
    .book-container {
        padding: 20px;
        /*background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);*/
        /*min-height: 800px;*/
        border-radius: 8px;
    }


    .book-display {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-bottom: 40px;
    }

    .page-indicator {
        position: absolute;
        top: -40px;
        right: 50%;
        transform: translateX(50%);
        background: rgba(255, 255, 255, 0.9);
        padding: 8px 20px;
        border-radius: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        z-index: 10;
    }

    .current-page {
        font-size: 24px;
        font-weight: bold;
        color: #1890ff;
    }

    .total-pages {
        font-size: 16px;
        color: #666;
    }

    /* 书本样式 */
    .book {
        position: relative;
        width: 100%;
        height: 500px;
        perspective: 1500px;
        margin: 0 auto;
    }

    .book.flipping {
        pointer-events: none;
    }

    /* 页面基础样式 */
    .page {
        position: absolute;
        width: 50%;
        height: 100%;
        background: white;
        border: 1px solid #e8e8e8;
        overflow: hidden;
        transition: all 0.3s;
    }

    .left-page {
        left: 0;
        border-radius: 5px 0 0 5px;
        border-right: 1px solid #f0f0f0;
        transform-origin: right center;
    }

    .right-page {
        right: 0;
        border-radius: 0 5px 5px 0;
        border-left: 1px solid #f0f0f0;
        transform-origin: left center;
    }

    .page-content {
        position: relative;
        width: 100%;
        height: 100%;
        padding: 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .page-image {
        max-width: 100%;
        max-height: 80%;
        object-fit: contain;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .page-title {
        margin-top: 20px;
        font-size: 18px;
        font-weight: 500;
        color: #333;
        text-align: center;
    }

    .page-number {
        position: absolute;
        font-size: 14px;
        color: #999;
        padding: 4px 12px;
        background: rgba(255, 255, 255, 0.8);
        border-radius: 12px;
    }

    .page-number.left {
        bottom: 10px;
        left: 10px;
    }

    .page-number.right {
        bottom: 10px;
        right: 10px;
    }

    /* 翻页样式 */
    .flip-page {
        position: absolute;
        width: 50%;
        height: 100%;
        right: 0;
        top: 0;
        transform-style: preserve-3d;
        cursor: pointer;
        z-index: 10;
    }

    .flip-page.left-flip {
        left: 0;
        right: auto;
        transform-origin: left center;
    }

    .flip-page-front,
    .flip-page-back {
        position: absolute;
        width: 100%;
        height: 100%;
        backface-visibility: hidden;
        border-radius: 0 5px 5px 0;
        overflow: hidden;
        background: white;
        border: 1px solid #e8e8e8;
    }

    .flip-page.left-flip .flip-page-front,
    .flip-page.left-flip .flip-page-back {
        border-radius: 5px 0 0 5px;
    }

    .flip-page-back {
        transform: rotateY(180deg);
    }

    .flip-page.left-flip .flip-page-back {
        transform: rotateY(-180deg);
    }

    .flip-content {
        width: 100%;
        height: 100%;
        padding: 15px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .flip-image {
        max-width: 90%;
        max-height: 70%;
        object-fit: contain;
        border-radius: 4px;
    }

    .flip-page-number {
        position: absolute;
        bottom: 10px;
        right: 10px;
        font-size: 12px;
        color: #999;
        background: rgba(255, 255, 255, 0.8);
        padding: 2px 8px;
        border-radius: 10px;
    }

    .flip-page.left-flip .flip-page-number {
        right: auto;
        left: 10px;
    }

    /* 缩略图导航 */
    .thumbnail-nav {
        margin-top: 40px;
        padding: 20px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .thumbnail-nav h4 {
        margin-bottom: 16px;
        color: #333;
    }

    .thumbnail-container {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;
    }

    .thumbnail-item {
        width: 80px;
        height: 100px;
        position: relative;
        cursor: pointer;
        border: 2px solid transparent;
        border-radius: 4px;
        overflow: hidden;
        transition: all 0.3s;
    }

    .thumbnail-item:hover {
        transform: translateY(-5px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    .thumbnail-item.active {
        border-color: #1890ff;
        box-shadow: 0 0 10px rgba(24, 144, 255, 0.5);
    }

    .thumbnail-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .thumbnail-label {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        text-align: center;
        padding: 2px;
        font-size: 12px;
    }

    /* 配置面板 */
    .config-panel {
        margin-top: 30px;
        background: white;
        border-radius: 8px;
    }

    .config-content {
        padding: 16px;
    }

    .config-item {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        flex-wrap: wrap;
    }

    .config-label {
        min-width: 120px;
        color: #666;
    }

    .config-value {
        margin-left: 16px;
        color: #1890ff;
        font-weight: 500;
    }

    /* 响应式设计 */
    @media (max-width: 992px) {
        .book {
            width: 600px;
            height: 400px;
        }
    }

    @media (max-width: 768px) {
        .book {
            width: 400px;
            height: 300px;
        }

        .thumbnail-item {
            width: 60px;
            height: 80px;
        }
    }

    @media (max-width: 480px) {
        .book {
            width: 300px;
            height: 250px;
        }

        .book-controls {
            flex-direction: column;
            gap: 10px;
        }

        .config-item {
            flex-direction: column;
            align-items: flex-start;
        }

        .config-label {
            margin-bottom: 8px;
        }
    }

    /* 动画效果 */
    @keyframes page-turn {
        0% {
            transform: rotateY(0);
        }
        100% {
            transform: rotateY(-180deg);
        }
    }

    @keyframes page-turn-left {
        0% {
            transform: rotateY(0);
        }
        100% {
            transform: rotateY(180deg);
        }
    }

    /* 翻页角落效果 */
    .flip-page::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 30px;
        height: 30px;
        background: linear-gradient(45deg, transparent 50%, rgba(0,0,0,0.05) 50%);
        z-index: 5;
    }

    .flip-page.left-flip::before {
        right: auto;
        left: 0;
        background: linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.05) 50%);
    }

    /* 书本阴影 */
    .book::after {
        content: '';
        position: absolute;
        top: 10px;
        left: 10px;
        right: 10px;
        bottom: 10px;
        background: rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        z-index: -1;
        transition: all 0.3s;
    }

    .book:hover::after {
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    }
</style>